import { useEffect, useRef, useState } from "react";
import { useStore } from "../store";
import { kidStyle } from "../kid-style";
import { longDate, signed } from "../format";
import { EventRow } from "./EventRow";
import { StarIcon } from "./StarIcon";
import { KidAvatar } from "./KidAvatar";
import type { HistoryDay } from "../../shared/history";
import { canSeeTaps } from "../../shared/roles";
import { timeOfDay } from "../../shared/time";
import type { DayRecord, KidSummary, PointEvent } from "../../shared/types";

/** One kid on the sheet's day. */
export interface DaySheetEntry {
  kid: KidSummary;
  day: HistoryDay;
}

/**
 * A bottom sheet for one calendar day, a block per chosen kid: where their score started and
 * ended, whether the day is closed and by whom, and every tap that day (for grown-ups and the
 * kid themselves; a sibling sees the score only). Tapping the backdrop, Close or Escape
 * dismisses it.
 */
export function DaySheet({
  date,
  entries,
  onClose,
}: {
  date: string;
  entries: DaySheetEntry[];
  onClose: () => void;
}) {
  const closeButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeButton.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // With one kid the whole sheet takes their colour, as it did before there were several.
  const only = entries.length === 1 ? entries[0].kid : null;

  return (
    <div className="sheet" style={only ? kidStyle(only.colour) : undefined}>
      <div className="sheet__backdrop" onClick={onClose} />
      <section className="sheet__panel" role="dialog" aria-modal="true" aria-labelledby="day-sheet-title">
        <div className="sheet__head">
          <h2 className="sheet__title" id="day-sheet-title">
            {longDate(date)}
          </h2>
          <button ref={closeButton} type="button" className="button button--small" onClick={onClose}>
            Close
          </button>
        </div>
        {entries.map((e) => (
          <DayKid key={e.kid.id} kid={e.kid} day={e.day} />
        ))}
      </section>
    </div>
  );
}

/** One kid's part of the day: score, close and taps, each read on its own. */
function DayKid({ kid, day }: DaySheetEntry) {
  const { snapshot, loadDayEvents, loadDays } = useStore();
  const canSee = snapshot ? canSeeTaps(snapshot.me, kid.id) : false;
  const [events, setEvents] = useState<PointEvent[] | null>(null);
  const [record, setRecord] = useState<DayRecord | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    Promise.all([
      canSee ? loadDayEvents(kid.id, day.day) : { events: [] },
      day.closed ? loadDays(kid.id, day.day, day.day) : [],
    ])
      .then(([taps, closed]) => {
        if (!live) return;
        setEvents(taps.events);
        setRecord(closed[0] ?? null);
      })
      .catch(() => live && setErr("Couldn't load that day."));
    return () => {
      live = false;
    };
  }, [kid.id, canSee, day.day, day.closed, loadDayEvents, loadDays]);

  const status = !day.closed
    ? "Still open"
    : !record
      ? "Closed"
      : record.closedBy === "auto"
        ? "Closed by itself"
        : `Closed by ${record.closedBy} at ${timeOfDay(record.closedAt)}`;

  return (
    <article className="day-kid" style={kidStyle(kid.colour)}>
      <div className="day-kid__head">
        <KidAvatar kid={kid} size="md" />
        <div className="day-kid__who">
          <h3 className="day-kid__name">{kid.name}</h3>
          <p className="day-kid__status">{status}</p>
        </div>
        {day.star && <StarIcon className="sheet__star" />}
      </div>
      <p className="sheet__score">
        {day.start} → {day.end}
        <span className="sheet__net">{signed(day.net)}</span>
      </p>
      {err && <p className="error">{err}</p>}
      {!canSee && <p className="hint">{`Only grown-ups and ${kid.name} can see ${kid.name}'s taps.`}</p>}
      {events &&
        canSee &&
        (events.length === 0 ? (
          <p className="hint">No taps this day.</p>
        ) : (
          <ul className="event-list">
            {events.map((e) => (
              <EventRow key={e.id} event={e} />
            ))}
          </ul>
        ))}
    </article>
  );
}
